import {
  useEffect,
  useReducer,
  useCallback,
  createContext,
  useState
} from "react";
import Uri from "jsuri";
import { isFunction, isEmpty, pick, get, omit, cloneDeep } from "lodash";
import Big from "big.js";
import axios from "axios";
import moment from "moment";
import i18n from "i18n/i18n";
import { StatusCodes } from "http-status-codes";
import { Alert } from "@texada/ui";
import { useDebounce } from "use-debounce";
import {
  isValidNumber,
  PAGE_NUMBER,
  SrmBoolean,
  ASSET_TYPE,
  getApiErrorMsg
} from "helpers/helper";
import { FlaskReq } from "helpers/Request";
import { useBarcodeScanner } from "helpers/BarcodeScanner";
import { FULL_DASHED_DATE_FORMAT, YEAR_TIME_FORMAT } from "helpers/timeHelpers";
import reducer, { types } from "./products/ProductSelectReducer";
import { getSetState, useRequest } from "helpers/hooks";
import {
  OVERRIDE_PRICE_URL,
  AVAILABILITY_URL,
  HEADOFFICE_OPTION_VALUE,
  ASSET_AVAILABILITY_URL,
  PART_AVAILABILITY_URL
} from "constants/AppConstants";
import { getProductPrice } from "helpers/urls";
import { getServiceSubTotal } from "pages/transactions/TransactionUtil";
import { UnitCostRenderer, AvailabilityRenderer } from "./renders/Renderers";
import "./ProductService.scss";
import DescriptionRenderer from "components/grid/renderers/DescriptionRenderer";

export type ContractState = any;
export type Asset = any;

export enum PRODUCT_MODE {
  ADD = "A",
  EDIT = "C",
  DELETE = "D"
}

export interface Product {
  contractDetailTrxN: string;
  type: string;
  product: string;
  description: string;
  detailTrxN: string;
  quantity: number;
  dateOut: string;
  timeOut: string;
  dateIn: string;
  timeIn: string;
  print: string;
  rates: {
    code: string;
    rate: number;
    rateCode: string;
  }[];
  meterCharge: any;
  availability: number;
  listPrice?: number;
  mode?: string;
  serialNumber?: string;
  discP?: number;
}

export const PART_COLUMNS = [
  {
    key: "product",
    name: i18n.t("product_number")
  },
  {
    key: "description",
    name: i18n.t("description"),
    cellRenderer: DescriptionRenderer,
    cellRendererParams: {
      showTooltipOverride: true
    }
  },
  {
    key: "availability",
    name: i18n.t("availability"),
    cellRenderer: AvailabilityRenderer
  },
  {
    key: "listPrice",
    name: i18n.t("price"),
    cellRenderer: UnitCostRenderer
  }
];

export const ProductSelectContext = createContext<{
  productsToSubmit: Models.ContractDetail[];
  updateProduct?: (selectedProduct: any, method: any) => void;
  addProduct?: (selectedProduct: any) => void;
  dispatch: Function;
  readonly: boolean;
  rentalDisc?: number;
  salesDisc?: number;
  state?: any;
}>(undefined);

export function normalizeProduct(product) {
  let newProduct = { ...product };
  newProduct.product = newProduct.productNumber;
  delete newProduct.productNumber;

  return newProduct;
}

export const PRODUCTS_OPTIONS = [
  { label: i18n.t("rental_products"), value: ASSET_TYPE.Rental },
  { label: i18n.t("sales_products"), value: ASSET_TYPE.Sales }
];

export const CUSTOMER_OWNED_OPTIONS = [
  { label: i18n.t("customer_owned"), value: ASSET_TYPE.CustomerOwned }
];

export function useProductSelectReducer() {
  const initialState = {
    products: [],
    availableProducts: [],
    page: PAGE_NUMBER.ONE,
    pages: PAGE_NUMBER.ONE,
    query: "",
    productsToSubmit: [],
    dataTimestamp: Date.now(),
    location: [],
    product_specs: "",
    status_code: [],
    product_class: "",
    product_group: "",
    showAvailable: false,
    kit: "",
    rates: {}
  };
  const [state, dispatch] = useReducer<React.Reducer<typeof initialState, any>>(
    reducer,
    initialState
  );

  const setState = getSetState(dispatch);

  return { state, dispatch, setState };
}

export function useProductsWithAvailability({ type, search = "", state }) {
  const [fetchFlaskProducts] = useRequest();
  const [fetchSrmProducts] = useRequest({ flask: false });
  const [fetchAvailability] = useRequest({ flask: false });
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAvailabilityData, setIsLoadingAvailabilityData] =
    useState(false);
  const [totalPages, setTotalPages] = useState(PAGE_NUMBER.ONE);
  const [debouncedSearch] = useDebounce(search, 200);
  const [availableError, setAvailableError] = useState("");

  useEffect(() => {
    setIsLoading(true);
    if (state.showAvailable) {
      fetchFromSRM();
      return;
    }
    fetchFromFlask();
  }, [
    type,
    debouncedSearch,
    state.page,
    state.location,
    state.dateOut,
    state.dateIn,
    state.product_class,
    state.manufacture_date_from,
    state.manufacture_date_to,
    state.product_specs,
    state.product_group,
    state.showAvailable,
    state.status_code,
    state.kit
  ]);

  async function fetchFromFlask() {
    const url = new Uri(`${localStorage.flask_url}/${type}/`)
      .addQueryParam("per_page", state.pageSize || PAGE_NUMBER.FIVE)
      .addQueryParam("page", state.page)
      .addQueryParam("exclude_non_bulk_disposal", true)
      .addQueryParam("inactive", SrmBoolean.False)
      .addQueryParam(
        "expand",
        [
          "serialNumbers",
          "locations",
          "rerent",
          "asset_document_status"
        ].toString()
      );

    if (debouncedSearch !== "") {
      url.addQueryParam("search", debouncedSearch);
    }

    [
      "location",
      "product_class",
      "manufacture_date_from",
      "manufacture_date_to",
      "product_specs",
      "product_group",
      "kit"
    ].forEach(key => {
      if (!isEmpty(state[key])) {
        if (Array.isArray(state[key]) && state[key].length > 0) {
          url.addQueryParam(
            key,
            state[key].map(e => e.value)
          );
        } else if (moment.isMoment(state[key])) {
          url.addQueryParam(key, state[key].format(FULL_DASHED_DATE_FORMAT));
        } else {
          url.addQueryParam(key, state[key]);
        }
      }
    });

    if (state.status_code && type === "assets") {
      url.addQueryParam("status_code", state.status_code);
    }
    try {
      const res = await fetchFlaskProducts(url.toString());

      if (res?.data !== undefined) {
        const products = (
          type === "parts"
            ? res.data.parts.map(part => {
                part.type = ASSET_TYPE.Sales;
                return part;
              })
            : res.data.assets.map(asset => {
                asset.type = ASSET_TYPE.Rental;
                return asset;
              })
        ).map(normalizeProduct);

        setTotalPages(res.data.pages.pages);
        setProducts(products);
        setAvailableError("");
        fetchProductAvailability(products);
      }
      setIsLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
        setIsLoading(false);
      }
    }
  }

  async function fetchProductAvailability(productList) {
    const selectedLocation =
      state.location.length === 0
        ? HEADOFFICE_OPTION_VALUE
        : state.location[0].value;

    const fetchUrl =
      type === "parts" ? PART_AVAILABILITY_URL : ASSET_AVAILABILITY_URL;

    const url = new Uri(fetchUrl)
      .addQueryParam("avail_from_date", state.dateOut)
      .addQueryParam("avail_to_date", state.dateIn)
      .addQueryParam("avail_from_time", state.timeOut)
      .addQueryParam("avail_to_time", state.timeIn)
      .addQueryParam("avail_location", selectedLocation)
      .addQueryParam(
        "product_numbers",
        [...new Set(productList.map(product => product.product))].join(",")
      );
    setIsLoadingAvailabilityData(true);
    try {
      const res = await fetchAvailability(url.toString());
      if (!res.data) {
        return;
      }

      const updatedProducts = productList.map(product => {
        const availability = res.data.find(
          item => item.number === product.product
        );
        if (availability) {
          return { ...product, availability };
        }
        return product;
      });
      setProducts(updatedProducts);
      setIsLoadingAvailabilityData(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
      }
    }
  }

  async function fetchFromSRM() {
    if (state.location.length > 1) return;
    const selectedLocation = state.location.length === 0 ? " " : state.location;
    let queryHeader = {
      page: state.page,
      limit: state.pageSize || PAGE_NUMBER.FIVE,
      fromDate: state.dateOut,
      fromTime: state.timeOut,
      toDate: state.dateIn,
      toTime: state.timeIn,
      locOrAll: selectedLocation,
      rentalOrSale: type === "parts" ? "S" : "R",
      classOrAll: "ALL",
      groupOrAll: "ALL",
      kitOrAll: "ALL",
      statusCodeOrAll: "ALL",
      sortByAvailableQuantity: SrmBoolean.True,
      includeBasedOnTag: SrmBoolean.False,
      activeOnly: SrmBoolean.True,
      responseType: "json"
    };
    if (type === "assets" && state.status_code?.length > 0) {
      queryHeader.statusCodeOrAll = state.status_code;
    }
    if (state.product_class) {
      queryHeader.classOrAll = state.product_class;
    }
    if (state.product_specs) {
      queryHeader.specs = state.product_specs;
    }
    if (state.product_group) {
      queryHeader.groupOrAll = state.product_group;
    }
    if (state.kit) {
      queryHeader.kitOrAll = state.kit;
    }
    if (state.location?.length > 0) {
      queryHeader.locOrAll = state.location.map(e => e.value).join(",");
    }
    if (state.manufacture_date_from) {
      queryHeader.manufacturedYear =
        state.manufacture_date_from.format(YEAR_TIME_FORMAT);
    }
    if (debouncedSearch !== "") {
      queryHeader.description = debouncedSearch;
    }

    try {
      const srmRes = await fetchSrmProducts(AVAILABILITY_URL, {
        headers: queryHeader
      });
      const srmData = JSON.parse(srmRes.data);
      let availabilityData = {};
      srmData.availability.forEach(e => {
        availabilityData[e.product.trim()] = {
          ...e,
          availability: e.availabilityNA,
          number: e.product.trim(),
          quantity: e.productAvailable
        };
      });

      const res = await FlaskReq({
        method: "get",
        url: new Uri(`${localStorage.flask_url}/${type}/`)
          .addQueryParam(
            "product_number_list",
            Object.keys(availabilityData).join(",")
          )
          .addQueryParam(
            "expand",
            [
              "serialNumbers",
              "locations",
              "rerent",
              "asset_document_status"
            ].toString()
          )
          .toString()
      });

      const products = (
        type === "parts"
          ? res.data.parts.map(part => {
              part.type = ASSET_TYPE.Sales;
              part.availability = availabilityData[part.productNumber.trim()];
              return part;
            })
          : res.data.assets.map(asset => {
              asset.type = ASSET_TYPE.Rental;
              asset.availability = availabilityData[asset.productNumber.trim()];
              return asset;
            })
      ).map(normalizeProduct);

      const availProducts = [];
      srmData.availability.forEach(e => {
        const target = products?.find(
          p => p.product.trim() === e.product.trim()
        );
        if (!target) return;
        availProducts.push({
          ...target,
          availability: {
            ...e,
            availability:
              e.availabilityNA === SrmBoolean.True
                ? SrmBoolean.False
                : SrmBoolean.True,
            number: e.product.trim(),
            quantity: e.productAvailable
          }
        });
      });
      setAvailableProducts(availProducts);
      setTotalPages(srmData.totalPages);
      setAvailableError("");
      setIsLoading(false);
    } catch (error) {
      let errorMessage = "";
      if (error?.response?.status === StatusCodes.GATEWAY_TIMEOUT) {
        errorMessage = i18n.t(
          "server_error_please_enter_more_parameters_to_narrow_down_search_or_try_later"
        );
        Alert.error({ message: errorMessage });
        setAvailableError(errorMessage);
      } else if (!axios.isCancel(error)) {
        errorMessage = getApiErrorMsg(error);
        Alert.error({ message: errorMessage });
        setAvailableError(errorMessage);
      }

      setAvailableProducts([]);
      setIsLoading(false);
    }
  }

  return {
    products,
    availableProducts,
    isLoading,
    isLoadingAvailabilityData,
    totalPages,
    availableError
  };
}

export function useRates({
  products,
  availableProducts,
  isFetchingData,
  readonly,
  dateOut,
  timeOut,
  dateIn,
  timeIn,
  division,
  customer,
  setState
}) {
  const [rates, setRates] = useState({});

  useEffect(() => {
    if (
      isFetchingData ||
      (products.length < 1 && availableProducts.length < 1) ||
      readonly
    )
      return;
    getRates([...availableProducts, ...products]);
  }, [products, availableProducts, isFetchingData, readonly]);

  async function getRates(productList) {
    let newRates = cloneDeep(rates);

    const processRates = async () => {
      for (const item of productList) {
        if (newRates.hasOwnProperty(item.product)) return;
        try {
          const res = await axios.get(getProductPrice({ id: item.product }), {
            headers: {
              fromDate: dateOut,
              fromTime: timeOut,
              toDate: dateIn,
              toTime: timeIn,
              division,
              customer: customer || ""
            },
            responseType: "json"
          });
          newRates[item.product] = res.data;
        } catch (err) {
          newRates[item.product] = { standardRate: 0 };
        }
        setRates(newRates);
        setState({ rates: newRates });
      }
    };
    await processRates();
  }
}

export function useProductSelectBarcodeScanner({
  view,
  getRate,
  dispatch,
  updateProduct
}: {
  view: boolean;
  dispatch: Function;
  getRate?: Function;
  updateProduct?: Function;
}) {
  useBarcodeScanner(
    async (event, barcode) => {
      if (view) return;
      async function getProduct(url) {
        try {
          const {
            data: {
              assets,
              parts,
              pages: { total }
            }
          } = await FlaskReq({ url, method: "get" });

          if (total !== PAGE_NUMBER.ONE) return false;

          let item = (assets || parts)[0];
          const product = normalizeProduct(item);

          if (updateProduct) {
            updateProduct(product, types.ADD_PRODUCT);
          } else {
            dispatch({
              type: types.ADD_PRODUCT,
              payload: {
                ...product
              }
            });
          }

          return product;
        } catch (e) {
          return false;
        }
      }

      const generateUrl = type =>
        `${localStorage.flask_url}/${type}/?barcode=${barcode}`;
      const product =
        (await getProduct(generateUrl("assets"))) ||
        (await getProduct(generateUrl("parts")));

      if (product && isFunction(getRate)) {
        getRate(product);
      }
    },
    [view, getRate, dispatch]
  );
}

export function getProductPostData(product, state = null, divisionConfig = {}) {
  let selectedProduct: any = {
    ...pick(product, [
      "contractDetailTrxN",
      "type",
      "product",
      "description",
      "quantity",
      "meterCharge",
      "availability",
      "mode",
      "kitNumber",
      "index",
      "rate",
      "listPrice",
      "kitDetails",
      "parentKitNumber",
      "childKitNumber",
      "serialNumbers",
      "kitQuantity",
      "kitIndex",
      "autoAdd",
      "allow_rental_discount",
      "allow_sales_discount",
      "discountP",
      "substitution",
      "meter",
      "meterReason",
      "oldClosingMeter",
      "newInitialMeter",
      "newMeterDigits",
      "unitPrice",
      "rateOverride",
      "createPurchaseOrderInfo",
      "rerent",
      "productClass",
      "fromSslModal"
    ]),
    product: product.product.trim(),
    detailTrxN: product.detailTrxN || undefined,
    dateOut: product.dateOut || "",
    timeOut: product.timeOut || "",
    dateIn: product.dateIn || "",
    timeIn: product.timeIn || "",
    print:
      divisionConfig?.contract_line_default_print?.trim() || SrmBoolean.True,
    originPrice: product.originPrice || product.listPrice
  };

  if (product.rates) {
    selectedProduct.rates = product?.rates?.map(p => {
      p.code = p.rateCode;
      return p;
    });
  }

  if (product.serialNumber) {
    selectedProduct.serialNumber = product.serialNumber;
  }

  if (product.bulkItem) {
    selectedProduct.bulkItem = product.bulkItem;
  }

  if (product.discountP) {
    selectedProduct.discP = product.discountP;
  }

  if (product.childKitNumber) {
    selectedProduct = {
      ...selectedProduct,
      ...pick(product, [
        "itemType",
        "itemNumber",
        "itemDescription",
        "quantity",
        "chargeable",
        "discountP",
        "print"
      ]),
      chargeable: product.chargeable ? SrmBoolean.True : SrmBoolean.False,
      add: SrmBoolean.True
    };
  }

  if (selectedProduct.originPrice === selectedProduct.listPrice && state) {
    delete selectedProduct.listPrice;
  }

  if (state?.defaultTax1Code && !selectedProduct?.tax1Code?.trim()) {
    selectedProduct.tax1Code = state.defaultTax1Code.trim();
  }

  if (state?.defaultTax2Code && !selectedProduct?.tax2Code?.trim()) {
    selectedProduct.tax2Code = state.defaultTax2Code.trim();
  }

  if (product?.detailTrxN?.trim()) {
    delete selectedProduct.substitution;
  }
  return selectedProduct;
}

export function getContractSubtotal({
  productsToSubmit = [],
  services = [],
  state = {},
  isCreate = false
}) {
  const getContractPrice = p => {
    if (p.type === ASSET_TYPE.Rental && isEmpty(p?.detailTrxN)) {
      return isValidNumber(p.rate) ? p.rate : 0;
    }
    return isValidNumber(p.listPrice)
      ? p.listPrice
      : isValidNumber(p.rate)
      ? p.rate
      : 0;
  };

  const getPrice = getContractPrice;

  function getProductTotal(type) {
    return productsToSubmit
      .filter(p => p.type === type)
      .map(p => {
        const { quantity, extendedAmount, discountAmount } = p;
        if (isCreate) {
          const price = getPrice(p);
          const rate = Big(isValidNumber(price) ? price : 0);
          let value = isValidNumber(quantity) ? rate.times(quantity) : rate;

          if (
            p.type === ASSET_TYPE.Rental &&
            p.allow_rental_discount !== SrmBoolean.False
          ) {
            const discount =
              Number(p.discountP) > 0
                ? Number(p.discountP)
                : Number(state.rentalDiscP);
            value = value.times(100 - (Number(discount) || 0)).times(0.01);
          } else if (
            p.type === ASSET_TYPE.Sales &&
            p.allow_sales_discount !== SrmBoolean.False &&
            Number(p.listPrice) === Number(p.originPrice)
          ) {
            const discount =
              Number(p.discountP) > 0
                ? Number(p.discountP)
                : Number(state.saleDiscP);
            value = value.times(100 - (Number(discount) || 0)).times(0.01);
          }
          return Number(value.toFixed(2));
        }
        return Number(Big(extendedAmount).plus(discountAmount).toFixed(2));
      })
      .filter(Number.isFinite)
      .reduce((accumulator, currVal) => {
        try {
          accumulator = accumulator.plus(currVal);
        } catch (e) {
          // big.js throws an error if the number is invalid
        }
        return accumulator;
      }, Big(0));
  }

  let rentalTotal = getProductTotal(ASSET_TYPE.Rental);
  let salesTotal = getProductTotal(ASSET_TYPE.Sales);
  let subtotal = Big(state?.salesAmount || 0).plus(state?.rentalsAmount || 0);
  if (isCreate) {
    subtotal = rentalTotal.plus(salesTotal);
  }
  const serviceTotal = getServiceSubTotal(services);
  return {
    subtotal: subtotal.toFixed(2),
    rentalTotal,
    salesTotal,
    serviceTotal
  };
}

export function useGetRate(
  dispatch,
  products,
  view,
  { dateOut, timeOut, dateIn, timeIn, division }
) {
  const getRate = useCallback(
    async product => {
      let putData: {
        rates?: {
          code: string;
          rate: number;
          rateCode: string;
        }[];
      } = {};

      if (Array.isArray(product?.rates)) {
        putData.rates = product.rates.map(p => {
          p.rateCode = p.code || p.rateCode;
          return p;
        });
      }

      if (!view) {
        // Need to update product's value. Because, date/time can be updated.
        product.dateOut = dateOut;
        product.timeOut = timeOut;
        product.dateIn = dateIn;
        product.timeIn = timeIn;
      }

      try {
        const res = await axios.put(
          `${OVERRIDE_PRICE_URL}${encodeURIComponent(product.product)}`,
          putData,
          {
            headers: {
              fromDate: product.dateOut,
              fromTime: product.timeOut,
              toDate: product.dateIn,
              toTime: product.toTime,
              division
            },
            responseType: "json"
          }
        );
        const rate =
          res?.data?.rate ||
          get(
            (res?.data?.rates || []).find(item => {
              return String(item?.rateCode) === String(res?.data?.rateCode);
            }) || {},
            "rate",
            0
          );
        const isDateSame = product.dateOut === product.dateIn;
        const isTimeSame = product.timeOut === product.timeIn;

        dispatch({
          type: types.GET_PRICE,
          payload: {
            productIndex: product.index,
            rate: isDateSame && isTimeSame ? res?.data?.rate : rate
          }
        });
      } catch (e) {}
    },
    [dispatch, dateOut, timeOut, dateIn, timeIn, division]
  );

  useEffect(
    function getAllRates() {
      products.forEach(product => {
        if (product?.chargeable === SrmBoolean.False) return;
        if (
          product?.fromKit?.kitN.trim() &&
          Number(product?.extendedAmount) === 0
        )
          return;
        getRate(product);
      });
    },
    [getRate]
  );

  return getRate;
}

export const existProduct = (productsToSubmit, productNumber) => {
  const foundProduct = productsToSubmit.find(
    p => p.product.trim() === productNumber
  );
  return foundProduct;
};

export async function processProduct(
  dispatch,
  productsToSubmit,
  state,
  product,
  isCreate,
  method = types.ADD_PRODUCT
) {
  let target = { ...product, mode: PRODUCT_MODE.ADD };

  if (!target?.type && target?.assetOrPart) {
    target.type = target.assetOrPart;
  }

  const exist = existProduct(productsToSubmit, target.product);
  if (exist) {
    if (exist?.bulkItem === SrmBoolean.False && target.isSwap !== true) {
      Alert.error({
        message: i18n.t("non_bulk_rental_can_only_have_a_quantity_of_1")
      });
      return "error";
    }

    if (method === types.EDIT_QUANTITY) {
      target.quantity = product.newQty ? product.newQty : exist.quantity + 1;
      target.mode = isCreate ? PRODUCT_MODE.ADD : PRODUCT_MODE.EDIT;
    }
  }

  if (isCreate) {
    if (
      !target?.substitution?.hasOwnProperty("rates") &&
      !target?.hasOwnProperty("unitPrice")
    ) {
      try {
        const res = await axios.get(
          getProductPrice({ id: encodeURIComponent(target.product) }),
          {
            headers: {
              fromDate: state.dateOut,
              fromTime: state.timeOut,
              toDate: state.dateIn,
              toTime: state.timeIn,
              division: state.division,
              customer: state.customerNumber || ""
            },
            responseType: "json"
          }
        );
        target.rates = res.data.rates;
        target.rate = res.data.rate;
      } catch (err) {
        target.rates = [];
        target.rate = 0;
      }
    }

    if (target.hasOwnProperty("chargeable") && target.chargeable === false) {
      target.rate = 0;
      target.listPrice = 0;
    }

    dispatch({
      type: method,
      payload: getProductPostData(target, null, state?.selectedDivisionConfig)
    });

    return null;
  }

  if (!exist || method !== types.EDIT_QUANTITY) {
    try {
      await axios.put(
        `${OVERRIDE_PRICE_URL}${encodeURIComponent(target.product)}`,
        {},
        {
          headers: {
            fromDate: state.dateOut,
            fromTime: state.timeOut,
            toDate: state.dateIn,
            toTime: state.timeIn,
            division: state.division
          },
          responseType: "json"
        }
      );
    } catch (err) {
      console.log(err);
    }

    target.availabilityQuantity = target.quantity;
    target.quantity = target.quantity || 1;

    target.dateOut = state.dateOut;
    target.timeOut = state.timeOut;
    target.dateIn = state.dateIn;
    target.timeIn = state.timeIn;
  }

  target = getProductPostData(target, state, state?.selectedDivisionConfig);
  if (method === types.EDIT_QUANTITY) {
    target = { ...omit(target, ["listPrice", "originPrice", "rate", "rates"]) };
    if (
      product.type === ASSET_TYPE.Rental &&
      product?.fromKit?.kitN.trim() &&
      Number(product?.extendedAmount) === 0
    ) {
      target.rate = 0;
    }
  }
  if (state?.meterInformation?.[target.product.trim()]) {
    target = {
      ...target,
      ...state.meterInformation[target.product.trim()]
    };
  }
  return target;
}

export function groupProducts(data) {
  let groupedProducts = data
    .filter(e => isEmpty(e.fromKit.detailTrxN))
    .map(e => ({ ...e, kitProducts: [] }));
  data.forEach(kit => {
    if (isEmpty(kit.fromKit.detailTrxN)) {
      return;
    }
    const targetIndex = groupedProducts.findIndex(
      product => product.detailTrxN.trim() === kit.fromKit.detailTrxN.trim()
    );
    groupedProducts.splice(targetIndex + 1, 0, kit);
  });
  return groupedProducts;
}
